import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import DOMPurify from 'isomorphic-dompurify';
import { apiService, apiHelpers } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './Reader.css';

const formatTimeRelative = (dateString) => {
  if (!dateString) return 'unknown time';
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return 'unknown time';
  }
};

const articleTopic = (article) => {
  if (article?.tags && article.tags.length > 0) return article.tags[0];
  return 'general';
};

const safeHttpUrl = (url) => (/^https?:\/\//i.test(url ?? '') ? url : null);

if (typeof DOMPurify.addHook === 'function') {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

const ArticlePage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getArticle(id);
        if (!cancelled) setArticle(response.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading article:', err);
        setError(apiHelpers.formatError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="container">
        <LoadingSpinner text="Loading article..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pb-drawer-page">
        <div className="pb-drawer">
          <Link to="/feed" className="pb-drawer-back">
            ← Back to front page
          </Link>
          <div className="pb-error" role="alert">
            <span>
              <strong>Error:</strong> {error}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pb-drawer-page">
        <div className="pb-drawer">
          <Link to="/feed" className="pb-drawer-back">
            ← Back to front page
          </Link>
          <h1 className="pb-drawer-headline">Story not found</h1>
          <p className="pb-drawer-blurb">
            The article you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  const tag = articleTopic(article);
  const time = formatTimeRelative(article.published);
  const tagList = article.tags || [];
  const safeLink = safeHttpUrl(article.link);
  const sanitizedSummary = article.summary
    ? DOMPurify.sanitize(article.summary, {
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'em', 'a', 'ul', 'ol', 'li',
          'blockquote', 'code', 'pre', 'h2', 'h3', 'h4', 'h5', 'h6',
        ],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
        ALLOW_DATA_ATTR: false,
        ADD_ATTR: ['target'],
        ADD_TAGS: [],
      })
    : '';

  return (
    <div className="pb-drawer-page">
      <div className="pb-drawer">
        <Link to="/feed" className="pb-drawer-back">
          ← Back to front page
        </Link>

        <div className="pb-drawer-eyebrow">
          <span className="pb-mono-tag">
            ▸ {tag.toUpperCase()} · {article.source_name || 'unknown source'} · {time}
          </span>
          {article.author && (
            <span className="pb-drawer-byline">By {article.author}</span>
          )}
        </div>

        <h1 className="pb-drawer-headline">{article.title || 'Untitled story'}</h1>

        {sanitizedSummary ? (
          <div
            className="pb-drawer-blurb"
            dangerouslySetInnerHTML={{ __html: sanitizedSummary }}
          />
        ) : (
          <p className="pb-drawer-blurb">No summary available for this article.</p>
        )}

        <div className="pb-drawer-why">
          <div className="pb-mono-tag">Why this ranked</div>
          <div className="pb-drawer-why-body">
            We don't write — we link. Surfaced from{' '}
            <strong>{article.source_name || 'the source'}</strong> because it matched your{' '}
            <strong>{tag}</strong> topic and is fresh ({time}).
          </div>
        </div>

        {safeLink && (
          <a
            href={safeLink}
            target="_blank"
            rel="noopener noreferrer"
            className="pb-drawer-cta"
          >
            Read original ↗
          </a>
        )}

        <div className="pb-drawer-section">
          <div className="pb-mono-tag">Read the originals · we link out</div>
          {safeLink ? (
            <a
              href={safeLink}
              target="_blank"
              rel="noopener noreferrer"
              className="pb-source-link"
            >
              <span>{article.source_name || 'Original source'}</span>
              <span className="pb-source-link-cta">OPEN ↗</span>
            </a>
          ) : (
            <div className="pb-empty" style={{ marginTop: 0 }}>
              No outbound link available.
            </div>
          )}
        </div>

        {tagList.length > 0 && (
          <div className="pb-drawer-section">
            <div className="pb-mono-tag">Related topics</div>
            <div className="pb-related">
              {tagList.slice(0, 8).map((relatedTag) => (
                <Link
                  key={relatedTag}
                  to={`/search?tags=${encodeURIComponent(relatedTag)}`}
                  className="pb-outlet-chip"
                >
                  {relatedTag}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="pb-drawer-section">
          <Link
            to={`/search?source=${encodeURIComponent(article.source_name || '')}`}
            className="pb-chip"
          >
            More from {article.source_name || 'this source'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;
